import React from 'react';
import { ExerciseImageGenerationAvailability } from './exerciseImageGenerationAvailability';
import { Alert, Box, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import {
    IVerbaScholaExerciseImageAsset, IVerbaScholaExerciseImageBrief,
    lessonAuthoringError, VerbaScholaApi,
} from '../api/VerbaScholaApi';
import { lessonImageAssetId, lessonImageUrl } from '../lessonImageUrl';
import { INTER, SLATE } from '../style/verbaScholaStyles';
import { isEarlierImagePass } from './exerciseImageReviewVersion';

interface Props {
    lessonId: number;
    label: string;
    value: string;
    altText: string;
    /** The URL in an AI proposal stays fixed while the selected candidate changes. */
    sourceUrl?: string;
    concept?: string;
    meaning?: string;
    lessonContext?: string;
    studentTask?: string;
    distractors?: string[];
    onChange: (url: string, altText: string) => void;
    onBusyChange?: (controlId: string, active: boolean) => void;
}

const reviewCheckLabels: Record<string, string> = {
    meaning: 'Intended meaning',
    essential_details: 'Essential visual details',
    distractor_separation: 'Distinct from other answers',
    card_clarity: 'Clear at answer-card size',
    unwanted_text: 'Unwanted writing',
    illustrative_style: 'Illustration style',
    observation_alignment: 'Visible details match the answer',
};

function reviewLabel(asset: IVerbaScholaExerciseImageAsset): string {
    if (asset.source === 'uploaded') return 'Teacher upload';
    const review = asset.quality_review;
    if (!review || review.status === 'unavailable') return 'AI review unavailable';
    if (isEarlierImagePass(review)) return 'Earlier AI pass — inspect';
    return review.accepted ? 'AI review found no issue' : 'Needs teacher review';
}

function reviewScore(asset: IVerbaScholaExerciseImageAsset): string | null {
    const score = asset.quality_review?.score;
    return typeof score === 'number' && Number.isFinite(score) ? `${Math.max(0, Math.min(100, Math.round(score)))}/100` : null;
}

function attemptRank(asset: IVerbaScholaExerciseImageAsset): number {
    const review = asset.quality_review;
    const status = (review?.status === 'passed' || review?.accepted) && !isEarlierImagePass(review) ? 2 : review?.status === 'failed' ? 0 : 1;
    const score = !isEarlierImagePass(review) && typeof review?.score === 'number' && Number.isFinite(review.score) ? review.score : 0;
    return status * 101 + Math.max(0, Math.min(100, score));
}

function mergeCandidates(known: IVerbaScholaExerciseImageAsset[], incoming: IVerbaScholaExerciseImageAsset[]): IVerbaScholaExerciseImageAsset[] {
    const byId = new Map<number, IVerbaScholaExerciseImageAsset>();
    for (const asset of known) byId.set(asset.id, asset);
    for (const asset of incoming) byId.set(asset.id, asset);
    return [...byId.values()].sort((left, right) => left.id - right.id);
}

/** Shared picker for saved activity editing and proposed pictures in the copilot. */
export default function VerbaScholaImageCandidateControls({ lessonId, label, value, altText, sourceUrl, concept = '', meaning = '', lessonContext = '', studentTask = '', distractors = [], onChange, onBusyChange }: Props) {
    const imageGenerationAvailability = React.useContext(ExerciseImageGenerationAvailability);
    const [candidates, setCandidates] = React.useState<IVerbaScholaExerciseImageAsset[]>([]);
    const [browseIndex, setBrowseIndex] = React.useState(0);
    const [imageConcept, setImageConcept] = React.useState(concept);
    const [imageMeaning, setImageMeaning] = React.useState(meaning);
    const [visualHint, setVisualHint] = React.useState('');
    const [teacherFeedback, setTeacherFeedback] = React.useState('');
    const [task, setTask] = React.useState(studentTask);
    const [otherAnswers, setOtherAnswers] = React.useState(distractors);
    const [uploadDescription, setUploadDescription] = React.useState('');
    const [guidanceOpen, setGuidanceOpen] = React.useState(false);
    const [generationNotice, setGenerationNotice] = React.useState<string | null>(null);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [recoveryHint, setRecoveryHint] = React.useState(false);
    const [loadingCandidates, setLoadingCandidates] = React.useState(false);
    const [candidateLoadError, setCandidateLoadError] = React.useState(false);
    const [failedCandidateUrl, setFailedCandidateUrl] = React.useState<string | null>(null);
    const [loadRevision, setLoadRevision] = React.useState(0);
    const [nextBeforeId, setNextBeforeId] = React.useState<string | null>(null);
    const [loadingOlder, setLoadingOlder] = React.useState(false);
    const [olderLoadError, setOlderLoadError] = React.useState(false);
    const [recentCandidates, setRecentCandidates] = React.useState<IVerbaScholaExerciseImageAsset[] | null>(null);
    const [recentLoadError, setRecentLoadError] = React.useState(false);
    const [loadingRecent, setLoadingRecent] = React.useState(false);
    const [failedRecentUrls, setFailedRecentUrls] = React.useState<Set<string>>(new Set());
    const [generationAttempt, setGenerationAttempt] = React.useState(0);
    const [availabilityTimedOut, setAvailabilityTimedOut] = React.useState(false);
    const requestSequence = React.useRef(0);
    const candidatesRef = React.useRef<IVerbaScholaExerciseImageAsset[]>([]);
    const inFlight = React.useRef(false);
    const activityId = React.useId();
    const busyChange = React.useRef(onBusyChange);
    busyChange.current = onBusyChange;
    const guidanceTouched = React.useRef(false);
    const conceptTouched = React.useRef(false);
    const meaningTouched = React.useRef(false);
    const mounted = React.useRef(true);
    const uploadInput = React.useRef<HTMLInputElement>(null);
    const source = sourceUrl || value;
    const currentSource = React.useRef(source);
    currentSource.current = source;
    const assetId = lessonImageAssetId(source);
    const distractorKey = distractors.join('\u001f');

    React.useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; requestSequence.current += 1; busyChange.current?.(activityId, false); };
    }, [activityId]);
    React.useEffect(() => {
        if (imageGenerationAvailability !== 'checking') { setAvailabilityTimedOut(false); return; }
        const timer = window.setTimeout(() => setAvailabilityTimedOut(true), 15000);
        return () => window.clearTimeout(timer);
    }, [imageGenerationAvailability]);
    React.useEffect(() => { if (!guidanceTouched.current && studentTask) setTask(studentTask); }, [studentTask]);
    React.useEffect(() => { if (distractorKey) setOtherAnswers(distractorKey.split('\u001f')); }, [distractorKey]);
    React.useEffect(() => { if (!conceptTouched.current) setImageConcept(concept); }, [concept]);
    React.useEffect(() => { if (!meaningTouched.current) setImageMeaning(meaning); }, [meaning]);

    const hydrateBrief = React.useCallback((brief?: IVerbaScholaExerciseImageBrief | null) => {
        if (!brief) return;
        if (!concept.trim() && brief.concept && !conceptTouched.current) setImageConcept(brief.concept);
        if (!meaning.trim() && brief.meaning && !meaningTouched.current) setImageMeaning(brief.meaning);
        if (!guidanceTouched.current) {
            if (brief.visual_hint) setVisualHint(brief.visual_hint);
            if (brief.student_task) setTask(brief.student_task);
            if (brief.distractors) setOtherAnswers(brief.distractors);
        }
    }, [concept, meaning]);

    React.useEffect(() => {
        if (assetId === null) { candidatesRef.current = []; setCandidates([]); setBrowseIndex(0); setLoadingCandidates(false); setCandidateLoadError(false); setOlderLoadError(false); setNextBeforeId(null); return; }
        if (inFlight.current) { setLoadingCandidates(false); return; }
        let cancelled = false;
        const fetchSequence = requestSequence.current;
        const sameGroup = candidatesRef.current.some(candidate => candidate.url === source);
        setLoadingCandidates(true);
        setCandidateLoadError(false);
        if (!sameGroup) { candidatesRef.current = []; setCandidates([]); setNextBeforeId(null); setOlderLoadError(false); }
        void VerbaScholaApi.getLessonImageCandidates(lessonId, assetId, source).then(result => {
            if (cancelled || inFlight.current || fetchSequence !== requestSequence.current) return;
            const list = mergeCandidates(sameGroup ? candidatesRef.current : [], result.data.candidates ?? []);
            candidatesRef.current = list;
            setCandidates(list);
            setBrowseIndex(Math.max(0, list.findIndex(candidate => candidate.url === value)));
            setNextBeforeId(result.data.next_before_id ?? null);
            setOlderLoadError(false);
            hydrateBrief(result.data.image_brief ?? list[0]?.image_brief);
        }).catch(() => {
            if (!cancelled && !inFlight.current && fetchSequence === requestSequence.current) setCandidateLoadError(true);
        }).finally(() => { if (!cancelled && !inFlight.current && fetchSequence === requestSequence.current) setLoadingCandidates(false); });
        return () => { cancelled = true; };
    }, [assetId, lessonId, hydrateBrief, loadRevision, source, value]);

    const current = candidates[browseIndex];
    const currentBrief = current?.image_brief;
    const reviewedMeaning = [currentBrief?.concept, currentBrief?.meaning].filter(Boolean).join(' — ');
    const select = (asset: IVerbaScholaExerciseImageAsset) => {
        setError(null);
        onChange(asset.url, asset.alt_text || altText);
    };
    const editCandidateBrief = (brief: IVerbaScholaExerciseImageBrief) => {
        guidanceTouched.current = true;
        conceptTouched.current = true;
        meaningTouched.current = true;
        setGuidanceOpen(true);
        setImageConcept(brief.concept ?? '');
        setImageMeaning(brief.meaning ?? '');
        setVisualHint(brief.visual_hint ?? '');
        setTask(brief.student_task ?? '');
        setOtherAnswers(brief.distractors ?? []);
        setTeacherFeedback('');
    };
    const generatedOrUploaded = (result: IVerbaScholaExerciseImageAsset) => {
        const list = mergeCandidates(candidatesRef.current, result.candidates?.length ? result.candidates : [result]);
        const previousUrls = new Set(candidatesRef.current.map(candidate => candidate.url));
        const newestIndex = list.map(candidate => !previousUrls.has(candidate.url)).lastIndexOf(true);
        const selectedIndex = Math.max(0, list.findIndex(candidate => candidate.url === result.url));
        candidatesRef.current = list;
        setCandidates(list);
        setBrowseIndex(newestIndex >= 0 && newestIndex !== selectedIndex ? newestIndex : selectedIndex);
        hydrateBrief(result.image_brief);
        setGenerationNotice(result.generation_warning || (newestIndex >= 0 && newestIndex !== selectedIndex
            ? 'New pictures were added. The highest-ranked picture is still selected; browse and choose a new one if you prefer it.' : null));
        setCandidateLoadError(false);
        onChange(result.url, result.alt_text || imageMeaning.trim() || altText);
    };
    const loadOlder = async () => {
        if (assetId === null || !nextBeforeId || loadingOlder || inFlight.current) return;
        const cursor = nextBeforeId;
        const sourceAtStart = source;
        const fetchSequence = requestSequence.current;
        const displayedUrl = candidatesRef.current[browseIndex]?.url ?? value;
        setLoadingOlder(true); setOlderLoadError(false);
        try {
            const result = await VerbaScholaApi.getLessonImageCandidates(lessonId, assetId, sourceAtStart, cursor);
            if (!mounted.current || inFlight.current || requestSequence.current !== fetchSequence || currentSource.current !== sourceAtStart) return;
            const list = mergeCandidates(candidatesRef.current, result.data.candidates ?? []);
            candidatesRef.current = list;
            setCandidates(list);
            setBrowseIndex(Math.max(0, list.findIndex(candidate => candidate.url === displayedUrl)));
            setNextBeforeId(result.data.next_before_id && result.data.next_before_id !== cursor ? result.data.next_before_id : null);
        } catch {
            if (mounted.current && requestSequence.current === fetchSequence && currentSource.current === sourceAtStart) setOlderLoadError(true);
        } finally { if (mounted.current) setLoadingOlder(false); }
    };
    const loadRecent = async () => {
        if (loadingRecent) return;
        setLoadingRecent(true); setRecentLoadError(false);
        try {
            const result = await VerbaScholaApi.getRecentLessonImages(lessonId);
            if (mounted.current) { setRecentCandidates(result.data.candidates ?? []); setFailedRecentUrls(new Set()); }
        } catch {
            if (mounted.current) setRecentLoadError(true);
        } finally { if (mounted.current) setLoadingRecent(false); }
    };
    const generate = async () => {
        if (imageGenerationAvailability !== true || !imageConcept.trim() || !imageMeaning.trim() || inFlight.current) return;
        const requestId = ++requestSequence.current;
        inFlight.current = true;
        busyChange.current?.(activityId, true);
        setBusy(true); setError(null); setRecoveryHint(false); setGenerationNotice(null);
        const savedAttempts: IVerbaScholaExerciseImageAsset[] = [];
        try {
            const startingOption = current?.url || value;
            let nextSourceUrl = lessonImageAssetId(startingOption) !== null ? startingOption : assetId !== null ? source : undefined;
            let nextFeedback = teacherFeedback.trim();
            const triedRevisions = new Set<string>();
            for (let attempt = 1; attempt <= 3 && mounted.current && requestId === requestSequence.current; attempt += 1) {
                setGenerationAttempt(attempt);
                const result = await VerbaScholaApi.generateLessonImage(lessonId, {
                    concept: imageConcept.trim(), meaning: imageMeaning.trim(),
                    lesson_context: lessonContext.trim().slice(0, 1500), visual_hint: visualHint.trim(),
                    student_task: task.trim().slice(0, 1500),
                    distractors: otherAnswers.map(item => item.trim().slice(0, 150)).filter(Boolean).slice(0, 8),
                    ...(nextSourceUrl ? { source_image_url: nextSourceUrl } : {}),
                    ...(nextFeedback ? { teacher_feedback: nextFeedback } : {}),
                });
                if (!mounted.current || requestId !== requestSequence.current) return;
                const asset = result.data;
                if (asset.generation_warning) { setGenerationNotice(asset.generation_warning); break; }
                savedAttempts.push(asset);
                generatedOrUploaded(asset);
                nextSourceUrl = asset.url;
                if (asset.quality_review?.status !== 'failed' || attempt === 3) break;
                const revised = asset.quality_review.revised_prompt?.trim();
                const revisionKey = revised?.replace(/\s+/gu, ' ').toLocaleLowerCase();
                // A fresh picture call is useful only when the reviewer supplied new guidance.
                if (!revised || revised.length > 400 || !revisionKey || triedRevisions.has(revisionKey)
                    || revisionKey === teacherFeedback.trim().replace(/\s+/gu, ' ').toLocaleLowerCase()) break;
                triedRevisions.add(revisionKey);
                nextFeedback = [teacherFeedback.trim().slice(0, 450), revised].filter(Boolean).join('\n').slice(0, 1000);
            }
        } catch (caught) {
            if (mounted.current && requestId === requestSequence.current) {
                setError(lessonAuthoringError(caught, 'Could not generate this picture. Check the prompt and try again.'));
                setRecoveryHint(true);
            }
        } finally {
            if (mounted.current && requestId === requestSequence.current && savedAttempts.length) {
                const best = savedAttempts.reduce((choice, asset) => attemptRank(asset) > attemptRank(choice) ? asset : choice);
                if (best.url !== savedAttempts[savedAttempts.length - 1].url) onChange(best.url, best.alt_text || imageMeaning.trim() || altText);
                setBrowseIndex(Math.max(0, candidatesRef.current.findIndex(asset => asset.url === best.url)));
            }
            inFlight.current = false;
            busyChange.current?.(activityId, false);
            if (mounted.current) { setBusy(false); setGenerationAttempt(0); setLoadRevision(value => value + 1); }
        }
    };
    const upload = async (file: File | undefined) => {
        if (!file || inFlight.current) return;
        setRecoveryHint(false);
        if (!['image/png', 'image/jpeg'].includes(file.type)) { setError('Choose a PNG or JPEG image.'); return; }
        if (file.size > 32 * 1024 * 1024) { setError('Choose a picture smaller than 32 MB.'); return; }
        if (!uploadDescription.trim() || uploadDescription.trim().length > 500) {
            setError('Describe the uploaded picture in 500 characters or fewer.'); return;
        }
        const requestId = ++requestSequence.current;
        inFlight.current = true;
        busyChange.current?.(activityId, true);
        setBusy(true); setError(null);
        try {
            const result = await VerbaScholaApi.uploadLessonImage(lessonId, file, uploadDescription.trim(), assetId !== null ? source : undefined);
            if (mounted.current && requestId === requestSequence.current) generatedOrUploaded(result.data);
        } catch (caught) {
            if (mounted.current && requestId === requestSequence.current) {
                setError(lessonAuthoringError(caught, 'Could not upload this picture. Try a PNG or JPEG image.'));
                setRecoveryHint(true);
            }
        } finally { inFlight.current = false; busyChange.current?.(activityId, false); if (mounted.current) { setBusy(false); setLoadRevision(value => value + 1); } }
    };

    return <Stack spacing={1}>
        {loadingCandidates && !busy && <Typography role="status" variant="caption">Loading picture options…</Typography>}
        {busy && generationAttempt > 0 && <Typography role="status" variant="caption">Generating and reviewing picture {generationAttempt} of up to 3…</Typography>}
        {candidateLoadError && <Alert severity="warning" action={<Button size="small" onClick={() => setLoadRevision(value => value + 1)}>Retry</Button>}>
            Could not load saved picture options. Retry to browse them.
        </Alert>}
        {olderLoadError && <Alert severity="warning">Could not load older picture options. Try again to continue browsing.</Alert>}
        {generationNotice && <Alert severity="info">{generationNotice}</Alert>}
        {current && <Box role="group" sx={{ p: 1, border: 1, borderColor: 'divider', borderRadius: 1, maxWidth: 340 }} aria-label={`${label} candidate options`}>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                <Typography variant="body2">Option {browseIndex + 1} of {candidates.length}</Typography>
                {/* Screen-reader-only. Pixel units are explicit: in sx, width 1 means 100%, which overflowed narrow screens. */}
                <Box component="span" role="status" aria-live="polite" aria-atomic="true"
                    sx={{ position: 'absolute', width: '1px', height: '1px', p: 0, m: '-1px', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0 }}>
                    Viewing picture {browseIndex + 1} out of {candidates.length}. {reviewLabel(current)}.
                </Box>
                <Chip size="small" label={reviewLabel(current)} color={isEarlierImagePass(current.quality_review) || current.quality_review?.status === 'failed' ? 'warning' : current.quality_review?.accepted ? 'success' : 'default'} />
                {reviewScore(current) && <Chip size="small" label={`${isEarlierImagePass(current.quality_review) ? 'Earlier AI score' : 'AI score'} ${reviewScore(current)}`} />}
            </Stack>
            <Box component="img" key={current.url} src={lessonImageUrl(current.url)} alt={current.alt_text || label} loading="lazy"
                onLoad={() => setFailedCandidateUrl(value => value === current.url ? null : value)}
                onError={() => setFailedCandidateUrl(current.url)}
                sx={{ display: failedCandidateUrl === current.url ? 'none' : 'block', width: '100%', maxHeight: 220, objectFit: 'contain', bgcolor: 'grey.100' }} />
            {failedCandidateUrl === current.url && <Alert severity="warning" sx={{ mt: 1 }}>
                This picture could not load. Choose another option or replace it before using the activity.
            </Alert>}
            {isEarlierImagePass(current.quality_review) && <Alert severity="warning" sx={{ mt: 1 }}>
                This picture passed an earlier AI check. The current image-only comparison has not reviewed it. Inspect the picture beside the exercise before confirming or replacing it.
            </Alert>}
            {reviewedMeaning && <Typography variant="caption" sx={{ display: 'block', mt: 1, overflowWrap: 'anywhere' }}>
                {isEarlierImagePass(current.quality_review) ? 'Intended meaning' : 'Reviewed for'}: {reviewedMeaning}
            </Typography>}
            {currentBrief?.visual_hint && <Typography variant="caption" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                Visual direction: {currentBrief.visual_hint}
            </Typography>}
            {currentBrief && <Button size="small" onClick={() => editCandidateBrief(currentBrief)} sx={{ mt: 0.5 }}>
                Edit this option's prompt
            </Button>}
            {current.quality_review?.rationale && <Typography variant="caption" sx={{ display: 'block', mt: 1, overflowWrap: 'anywhere' }}>{isEarlierImagePass(current.quality_review) ? 'Earlier AI note: ' : ''}{current.quality_review.rationale}</Typography>}
            {current.quality_review?.critical_checks?.filter(check => !check.passed).map((check, index) =>
                <Typography key={`${check.name}:${index}`} variant="caption" sx={{ display: 'block', mt: 0.5, overflowWrap: 'anywhere' }}>
                    Check to inspect: {reviewCheckLabels[check.name] ?? check.name.replaceAll('_', ' ')}{check.evidence ? ` — ${check.evidence}` : ''}
                </Typography>)}
            {current.quality_review?.revised_prompt && !current.quality_review.accepted && <Box sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                    Suggested next visual prompt: {current.quality_review.revised_prompt}
                </Typography>
                <Button size="small" onClick={() => {
                    guidanceTouched.current = true;
                    setGuidanceOpen(true);
                    setVisualHint(current.quality_review?.revised_prompt ?? '');
                }}>Use suggestion in visual prompt</Button>
            </Box>}
            {candidates.length > 1 && <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                <Button size="small" onClick={() => setBrowseIndex((browseIndex + candidates.length - 1) % candidates.length)}>Previous</Button>
                <Button size="small" onClick={() => setBrowseIndex((browseIndex + 1) % candidates.length)}>Next</Button>
                <Button size="small" variant="contained" disabled={busy || failedCandidateUrl === current.url} aria-pressed={current.url === value} onClick={() => { if (current.url !== value) select(current); }}>
                    {current.url === value ? 'Selected' : 'Use this picture'}
                </Button>
            </Stack>}
            {nextBeforeId && <Button size="small" disabled={busy || loadingOlder || loadingCandidates} onClick={() => void loadOlder()} sx={{ mt: 1 }}>
                {loadingOlder ? 'Loading older pictures…' : olderLoadError ? 'Retry older options' : 'Load older options'}
            </Button>}
            {candidates.some(candidate => reviewScore(candidate) !== null) && <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                AI scores are advisory and reflect each option's saved brief. Compare pictures for the same meaning and check their usefulness yourself.
            </Typography>}
        </Box>}
        <Box component="details" sx={{ '& summary': { cursor: 'pointer', fontFamily: INTER, color: SLATE, fontSize: 13 } }}>
            <Box component="summary">Recent saved pictures</Box>
            <Stack spacing={1} sx={{ pt: 1 }}>
                <Typography variant="caption">Find a picture saved in this lesson if you left the editor or a request ended before it appeared here. Check the meaning before choosing it.</Typography>
                <Button size="small" variant="outlined" disabled={loadingRecent || busy} onClick={() => void loadRecent()} sx={{ alignSelf: 'flex-start' }}>
                    {loadingRecent ? 'Loading recent pictures…' : recentCandidates === null ? 'Find recent pictures' : 'Refresh recent pictures'}
                </Button>
                {recentLoadError && <Alert severity="warning">Could not load recent pictures. Try again.</Alert>}
                {recentCandidates?.length === 0 && <Typography variant="caption">No recent generated or uploaded pictures were found for this lesson.</Typography>}
                {recentCandidates?.map(asset => <Stack key={asset.id} direction="row" spacing={1} alignItems="center" sx={{ border: 1, borderColor: 'divider', p: 0.75, borderRadius: 1 }}>
                    <Box component="img" src={lessonImageUrl(asset.url)} alt="" loading="lazy"
                        onError={() => setFailedRecentUrls(current => new Set(current).add(asset.url))}
                        sx={{ width: 56, height: 56, objectFit: 'contain', bgcolor: 'grey.100', flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="caption" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                            {[asset.image_brief?.concept, asset.image_brief?.meaning].filter(Boolean).join(' — ') || asset.alt_text}
                        </Typography>
                        <Typography variant="caption" sx={{ color: SLATE }}>{asset.source === 'uploaded' ? 'Uploaded picture' : reviewLabel(asset)}</Typography>
                        {failedRecentUrls.has(asset.url) && <Typography variant="caption" sx={{ display: 'block', color: 'error.main' }}>Picture could not load</Typography>}
                    </Box>
                    <Button size="small" disabled={busy || asset.url === value || failedRecentUrls.has(asset.url)} onClick={() => { select(asset); setRecentCandidates(null); }}>
                        {asset.url === value ? 'Selected' : 'Use picture'}
                    </Button>
                </Stack>)}
            </Stack>
        </Box>
        <Box component="details" open={guidanceOpen} onToggle={event => setGuidanceOpen((event.currentTarget as HTMLDetailsElement).open)} data-image-generator-guidance sx={{ '& summary': { cursor: 'pointer', fontFamily: INTER, color: SLATE, fontSize: 13 } }}>
            <Box component="summary">{value ? 'Edit picture prompt, regenerate, or upload' : 'Generate or upload a picture'}</Box>
            <Stack spacing={1} sx={{ pt: 1.25 }}>
                <TextField size="small" label="Target word or concept (not drawn as text)" value={imageConcept} onChange={event => { guidanceTouched.current = true; conceptTouched.current = true; setImageConcept(event.target.value); }} inputProps={{ maxLength: 200 }} fullWidth
                    helperText="Used to judge the picture against the lesson. The generator should not draw this text." />
                <TextField size="small" label="Meaning in this lesson" value={imageMeaning} onChange={event => { guidanceTouched.current = true; meaningTouched.current = true; setImageMeaning(event.target.value); }} inputProps={{ maxLength: 500 }} fullWidth
                    helperText="Use the intended sense of an ambiguous word." />
                <TextField size="small" label="Visual prompt (optional)" value={visualHint} onChange={event => { guidanceTouched.current = true; setVisualHint(event.target.value); }} inputProps={{ maxLength: 1500 }} fullWidth multiline minRows={2}
                    helperText="Describe useful visual details. Keep the concept and meaning above accurate." />
                <TextField size="small" label="What should change? (optional)" value={teacherFeedback} onChange={event => setTeacherFeedback(event.target.value)} inputProps={{ maxLength: 1000 }} fullWidth multiline minRows={2}
                    helperText="Give focused feedback for the next picture; you can leave this blank for a new variation." />
                <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 12 }}>Generation can make up to three candidates when AI review finds a problem. Review every picture before saving the activity.</Typography>
                {imageGenerationAvailability === false && <Alert severity="info">AI picture generation is turned off here. You can browse saved pictures or upload your own.</Alert>}
                {imageGenerationAvailability === 'checking' && (availabilityTimedOut
                    ? <Alert severity="warning">Could not verify picture generation availability. Refresh the lesson to try again; saved pictures and uploads still work.</Alert>
                    : <Typography role="status" variant="caption">Checking picture generation availability…</Typography>)}
                {imageGenerationAvailability !== false && !availabilityTimedOut && <Button variant="outlined" disabled={busy || imageGenerationAvailability !== true || !imageConcept.trim() || !imageMeaning.trim()} onClick={() => void generate()}>
                    {busy ? 'Generating and reviewing…' : value ? 'Regenerate picture' : 'Generate picture'}
                </Button>}
                <TextField size="small" label="Uploaded picture description" value={uploadDescription} onChange={event => { guidanceTouched.current = true; setUploadDescription(event.target.value); }} inputProps={{ maxLength: 500 }} fullWidth
                    helperText="Describe what your own picture shows for students using a screen reader." />
                <Button variant="outlined" disabled={busy || !uploadDescription.trim() || uploadDescription.trim().length > 500} onClick={() => uploadInput.current?.click()}>Upload picture</Button>
                <input ref={uploadInput} type="file" accept="image/png,image/jpeg" hidden aria-label={`Choose ${label.toLowerCase()} to upload`} onChange={event => {
                    const file = event.currentTarget.files?.[0]; event.currentTarget.value = '';
                    void upload(file);
                }} />
            </Stack>
        </Box>
        {error && <Alert severity="error">{error}{recoveryHint && <Typography variant="caption" sx={{ display: 'block' }}>If the picture was saved, find it under Recent saved pictures above.</Typography>}</Alert>}
    </Stack>;
}
